<template>
	<Modal fullscreen>
		<template #title>
			<div class="font-h3-bold text-ink-60">
				{{ isSendingDiscountsStep ? t('setters.sendDiscountsModal.title') : t('setters.confirmationModal.title') }}
			</div>
		</template>

		<div class="font-body-m mt-05 w-full max-w-[50rem] text-ink-87">
			<template v-if="isSendingDiscountsStep">
				<div>
					{{ t('setters.sendDiscountsModal.body.1') }}
				</div>

				<div class="mt-05">
					<div v-for="i in 3" :key="i" class="flex">
						<Icon class="pt-01 text-primary" size="1.5" name="square-small"></Icon>
						<div>
							<T :path="`setters.sendDiscountsModal.bullet.${i}`">
								<template #steam>
									<span class="font-bold text-primary">{{ t('setters.steam') }}</span>
								</template>

								<template #nintendo>
									<span class="font-bold text-primary">{{ t('setters.nintendo') }}</span>
								</template>

								<template #twoWorkingDays>
									<span class="font-bold text-primary">{{ t('setters.twoWorkingDays') }}</span>
								</template>

								<template #doubleCheck>
									<span class="font-bold text-primary">{{ t('setters.doubleCheck') }}</span>
								</template>
							</T>
						</div>
					</div>
				</div>

				<div class="mt-06">
					{{ t('setters.sendDiscountsModal.body.2') }}
				</div>

				<div class="mt-05">
					<div v-for="i in 2" :key="i" class="flex">
						<Icon class="pt-01 text-primary" size="1.5" name="square-small"></Icon>
						<div>
							<T :path="`setters.sendDiscountsModal.bullet.1.${i}`">
								<template #highImpactDiscounts>
									<span class="font-bold text-primary">{{ t('setters.highImpactDiscounts') }}</span>
								</template>

								<template #changesToEvents>
									<span class="font-bold text-primary">{{ t('setters.changesToEvents') }}</span>
								</template>
							</T>
						</div>
					</div>
				</div>

				<div class="mt-07 flex flex-col items-end">
					<div class="flex gap-04">
						<Button size="s" variant="tertiary" :disabled="isProcessing" @click="hideDiscountSettersModal">{{
							t('setters.sendDiscountsModal.cancel')
						}}</Button>

						<Button size="s" :loading="isProcessing" @click="submit">{{ t('setters.sendDiscountsModal.setDiscounts') }}</Button>
					</div>

					<div v-if="isError" class="font-paragraph-s mt-04 text-message-error">Something went wrong. Try again.</div>
				</div>
			</template>

			<template v-else>
				<div>
					{{ t('setters.confirmationModal.thanks') }} <br /><br />

					<T path="setters.confirmationModal.body.1">
						<template #steam>
							<span class="font-bold text-primary">{{ t('setters.steam') }}</span>
						</template>

						<template #nintendo>
							<span class="font-bold text-primary">{{ t('setters.nintendo') }}</span>
						</template>

						<template #twoWorkingDays>
							<span class="font-bold text-primary">{{ t('setters.twoWorkingDays') }}</span>
						</template>
					</T>

					<br /><br />
					{{ t('setters.confirmationModal.body.2') }}
				</div>

				<div class="mt-07 flex justify-end gap-04">
					<Button size="s" variant="tertiary" :disabled="isProcessing" @click="downloadSummary">{{
						t('setters.confirmationModal.downloadSummary')
					}}</Button>

					<Button size="s" :loading="isProcessing" @click="hideDiscountSettersModal">{{ t('setters.confirmationModal.proceed') }}</Button>
				</div>
			</template>
		</div>
	</Modal>
</template>

<script setup lang="ts">
import { Button, Icon, Modal, useTranslation } from '@bluebrick/saas-ui'
import { storeToRefs } from 'pinia'
import { computed, ref } from 'vue'
import { CSVDownload } from '@/common/utils'
import { useDiscountSettersStore } from '@/features/discountSetters/useDiscountSettersStore'
import { useNotificationsStore } from '@/features/notifications/useNotificationsStore'
import { useTelemetry } from '@/features/telemetry/useTelemetry'
import T from '@/translations/T.vue'

const { t } = useTranslation()
const { trackEvent, trackException } = useTelemetry()
const { sendEventsToSetters, hideDiscountSettersModal } = useDiscountSettersStore()
const { notifyError } = useNotificationsStore()
const { submittedEventsToDownload } = storeToRefs(useDiscountSettersStore())

const step = ref<'sending-discounts' | 'confirmation'>('sending-discounts')

const isSendingDiscountsStep = computed(() => step.value === 'sending-discounts')

const isProcessing = ref(false)
const isError = ref(false)

async function submit() {
	trackEvent('user', 'Submitted events for discount setters.')

	isProcessing.value = true
	isError.value = false

	try {
		await sendEventsToSetters()
		step.value = 'confirmation'
	} catch (e) {
		notifyError(`Failed to submit events to setters.`)
		isError.value = true
	} finally {
		isProcessing.value = false
	}
}

function downloadSummary() {
	trackEvent('user', 'Chose to download a summary of events send to discount setters.')

	if (submittedEventsToDownload.value) {
		let csvContent = 'Account name,Event name,Start date,End date,Discount depth,SKU name,SKU ID,Portal,Region,Status,Event create time\n'

		submittedEventsToDownload.value.forEach((event) => {
			let row = [
				event.account_name,
				`"${event.sale}"`,
				event.date_from,
				event.date_to,
				`"${event.discount}"`,
				`"${event.sku_name}"`,
				`"${event.sku_id}"`,
				event.portal,
				event.region,
				event.event_origin === 'scraped'
					? "set on portal's backend"
					: event.event_origin === 'customer_created'
						? 'planned; not sent'
						: event.event_origin,
				event.create_time
			]

			csvContent += row.join(',') + '\n'
		})

		CSVDownload(csvContent, 'discountSetters')
	} else {
		trackException(new Error('no events to download'), 'Download of events sent to setters failed, because there are no events to download')
		notifyError('Something went wrong. Try again or contact us.')
	}
}
</script>
